import { Select } from '@loadsmart/loadsmart-ui'
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { DataModelSchemaList } from 'common/types/kraken-core/DataModelSchema'
import { onSearchDataModelSchemas } from 'common/components/datasources/datasource'
import { useEffect, useState, useMemo } from 'react'
import { getDataModelSchema } from 'services/kraken-core/data_model/dataModel.service'

export interface DataModelSchemaSelectorProps {
  selected?: {
    label?: string
    id?: string
  }
  onSelect: (selected: DataModelSchemaList | null) => void
  disabled?: boolean
}

const DATASOURCES = {
  DATA_MODEL_SCHEMA: [
    () => ({
      type: 'string',
      adapter: {
        getKey: (schema: DataModelSchemaList) => schema.id || '',
        getLabel: (schema: DataModelSchemaList) => schema.description || schema.id,
      },
      fetch: onSearchDataModelSchemas,
    }),
  ],
}

export default function DataModelSchemaSelector({
  selected,
  onSelect,
  disabled = false,
}: DataModelSchemaSelectorProps) {
  const [selectedValue, setSelectedValue] = useState<Selectable | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Memoize the selected value to avoid unnecessary re-renders
  const memoizedSelected = useMemo(() => {
    if (selected?.id && selected?.label) {
      return {
        value: selected.id,
        label: selected.label,
      }
    }
    return null
  }, [selected?.id, selected?.label])

  // Handle initialization with ID only
  useEffect(() => {
    const initializeWithId = async () => {
      if (selected?.id && !selected?.label) {
        setIsLoading(true)
        try {
          const result = await getDataModelSchema(selected.id)
          if (!result.error && result.response?.data) {
            const schema = result.response.data
            const newSelected = {
              value: schema.id,
              label: schema.description || schema.id,
            }
            setSelectedValue(newSelected as Selectable)
          }
        } catch (error) {
          console.error('Error fetching data model schema:', error)
        } finally {
          setIsLoading(false)
        }
      } else if (memoizedSelected) {
        setSelectedValue(memoizedSelected as Selectable)
      } else {
        setSelectedValue(null)
      }
    }

    initializeWithId()
  }, [selected?.id, selected?.label, memoizedSelected])

  const handleChange = (e: any) => {
    const selectedItem = e.target.value as DataModelSchemaList
    setSelectedValue(
      selectedItem
        ? ({
            value: selectedItem.id,
            label: selectedItem.description || selectedItem.id,
          } as Selectable)
        : null
    )
    onSelect(selectedItem || null)
  }

  return (
    <Select
      name="select_data_model_schema"
      onChange={handleChange}
      value={selectedValue}
      datasources={DATASOURCES.DATA_MODEL_SCHEMA}
      placeholder="Select a Data Model Schema"
      disabled={disabled || isLoading}
    />
  )
}
